import React, { useMemo, useState } from 'react';
import LoadingSpinner from 'components/LoadingSpinner';
import { Button, Chip, Select, Table, TableColumn } from 'common/components';
import { UserRoundX } from 'lucide-react';
import { startOfDay, endOfDay } from 'date-fns';
import { useGetManagerCenterData } from 'services/hooks/queries';
import { useUpdateAgentAvailabileStatusById } from 'services/hooks/mutations';
import {
    useAgentsWithWorkflowAssignments,
    type WithWorkflowAssignments,
} from '../hooks/useAgentsWithWorkflowAssignments';

import styles from '../ManagerCommandCenter.module.scss';

type Agent = {
    id: string;
    name: string;
    status: string;
    calls_taken_today: number;
};

type AgentRow = WithWorkflowAssignments<Agent>;

interface AgentMonitoringTabProps {
    isManager: boolean;
}

const renderStatusBadge = (status: string) => {
    let badgeClass = 'manager-command-center-status-badge';

    switch (status) {
        case 'Call in Progress':
            badgeClass += ' manager-command-center-status-badge-active';
            break;
        case 'Available':
            badgeClass += ' manager-command-center-status-badge-queue';
            break;
        case 'Offline':
            badgeClass += ' manager-command-center-status-badge-inactive';
            break;
        default:
            badgeClass += ' manager-command-center-status-badge-inactive';
    }

    return <span className={badgeClass}>{status}</span>;
};

const getStatusPriority = (status: string) => {
    switch (status) {
        case 'Call in Progress':
            return 1;
        case 'Available':
            return 2;
        case 'Offline':
            return 3;
        default:
            return 4;
    }
};

const MetricCard = ({ label, value, color }: { label: string; value: number; color?: string }) => {
    const style = color ? { color } : {};
    return (
        <div className='metric-card'>
            <div className='metric-card-label'>{label}</div>
            <div className='metric-card-value' style={style}>
                {value}
            </div>
        </div>
    );
};

const AgentMonitoringTab: React.FC<AgentMonitoringTabProps> = ({ isManager }) => {
    const [statusFilter, setStatusFilter] = useState('All Statuses');

    const startOfDate = startOfDay(new Date());
    const endOfDate = endOfDay(new Date());

    const { managerCenterData, isManagerCenterDataLoading, isManagerCenterDataFetching } = useGetManagerCenterData({
        startDate: startOfDate,
        endDate: endOfDate,
    });

    const { updateAgentAvailabileStatusById } = useUpdateAgentAvailabileStatusById();

    const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setStatusFilter(e.target.value);
    };

    const uniqueStatuses = ['All Statuses', ...new Set(managerCenterData?.agents.map((agent) => agent.status))];

    const filteredAgents = useMemo(() => {
        if (!managerCenterData) return [];

        return statusFilter === 'All Statuses'
            ? [...managerCenterData.agents]
            : managerCenterData.agents.filter((agent) => agent.status === statusFilter);
    }, [managerCenterData, statusFilter]);

    const agentsWithWorkflows = useAgentsWithWorkflowAssignments(filteredAgents);

    const filteredAndSortedAgents: AgentRow[] = useMemo(
        () =>
            [...agentsWithWorkflows].sort((a, b) => {
                const statusPriorityA = getStatusPriority(a.status);
                const statusPriorityB = getStatusPriority(b.status);

                if (statusPriorityA !== statusPriorityB) {
                    return statusPriorityA - statusPriorityB;
                }

                return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
            }),
        [agentsWithWorkflows]
    );

    const columns: TableColumn<AgentRow>[] = [
        {
            key: 'name',
            title: 'Name',
            dataIndex: 'name',
        },
        {
            key: 'status',
            title: 'Status',
            render: (_, record) => renderStatusBadge(record.status),
        },
        {
            key: 'calls_taken_today',
            title: 'Calls Taken Today',
            dataIndex: 'calls_taken_today',
        },
        {
            key: 'workflows',
            title: 'Workflows',
            render: (_, record) => {
                if (!record.is_agent) {
                    return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
                }

                if (record.workflow_assignments.length === 0) {
                    return (
                        <Chip variant='filled' color='warning' size='sm'>
                            No workflow assignments — won&apos;t receive inbound calls
                        </Chip>
                    );
                }

                return (
                    <div className={styles.workflowChipsCell}>
                        {record.workflow_assignments.map((wf) => (
                            <Chip key={wf.id} variant='filled' color='info' size='sm'>
                                {wf.title}
                            </Chip>
                        ))}
                    </div>
                );
            },
        },
        {
            key: 'actions',
            title: '',
            align: 'right',
            render: (_, record) =>
                isManager && record.status === 'Available' ? (
                    <Button
                        variant='primary'
                        size='sm'
                        icon={UserRoundX}
                        onClick={() => updateAgentAvailabileStatusById({ id: record.id, status: false })}
                    >
                        Set Offline
                    </Button>
                ) : null,
        },
    ];

    if (isManagerCenterDataLoading || !managerCenterData) {
        return (
            <div className='loading'>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <>
            <div className='metrics-grid'>
                <MetricCard label='Calls Taken' value={managerCenterData.metrics.calls_taken} />
                <MetricCard label='Online Agents' value={managerCenterData.metrics.online_agents} color='#25D366' />
            </div>

            <div className='agent-monitoring-section'>
                <h2 className='section-title'>Agent Monitoring</h2>

                <div className='filters-bar'>
                    <Select
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        options={uniqueStatuses.map((status) => ({
                            value: status,
                            label: status,
                        }))}
                    />
                </div>

                <div className='agent-table-container'>
                    <Table
                        isLoading={isManagerCenterDataFetching}
                        data={filteredAndSortedAgents}
                        columns={columns}
                        rowKey='id'
                        hoverable
                        variant='secondary'
                        emptyText='No agents found'
                        className='agent-table'
                    />
                </div>
            </div>
        </>
    );
};

export default AgentMonitoringTab;
